import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Building2, User, Globe, Camera, Loader2, Save, Check, Sun, Moon, Users, DollarSign, Send, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const ALL_PAGES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'loads', label: 'Loads' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'drivers', label: 'Drivers' },
  { key: 'customers', label: 'Customers' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'fleet-map', label: 'Fleet Map' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'training', label: 'Training' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'payments', label: 'Payments' },
  { key: 'messages', label: 'Messages' },
];

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
];

export default function AdminSettings() {
  const { user, token, supabase } = useAuth();
  const { theme, setTheme } = useThemeContext();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [org, setOrg] = useState({ name: '', address: '', phone: '', logo_url: '' });
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [prefs, setPrefs] = useState({ timezone: 'America/Toronto', currency: 'CAD', distance_unit: 'km' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [invitePassword, setInvitePassword] = useState('');
  const [showInvitePassword, setShowInvitePassword] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [memberPermissions, setMemberPermissions] = useState({});
  const [savingPerms, setSavingPerms] = useState(null);
  const [pricing, setPricing] = useState({
    base_rate: '', weight_rate_per_lb: '', pieces_rate: '', vehicle_rate: '',
    after_hours_rate: '', waiting_rate_shipper: '', waiting_rate_consignee: '',
    insurance_rate: '', appointment_rate: '', misc_rate: '', hazmat_rate: '',
    signature_rate: '', pod_photo_rate: '',
    fuel_surcharge_pct: '', price_per_km: '', price_per_kg: '',
    rate_regular: '1.0', rate_rush: '1.5', rate_direct: '2.0',
    gst_rate: '5.0', hst_rate: '13.0', pst_rate: '0',
    default_tax_type: 'hst',
    max_width: '8.5', max_height: '13.5', max_length: '53',
    wait_time_per_hour: '75',
    wait_grace_minutes: '30', wait_min_increment: '15min', wait_auto_apply: false,
    fuel_auto_apply: false, fuel_apply_to: 'base_only', fuel_min_distance_km: '0',
    after_hours_start: '18:00', after_hours_end: '06:00',
    weekend_multiplier: '1.5', holiday_multiplier: '2.0',
    auto_generate_charges: true, auto_apply_tax: true,
  });
  const [pricingLoaded, setPricingLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
        Promise.all([
      api.get('/organizations').catch(() => ({ data: {} })),
      api.get('/profile').catch(() => ({ data: {} })),
      api.get('/users').catch(() => ({ data: [] })),
      api.get('/pricing-rules').catch(() => ({ data: {} })),
    ]).then(([orgRes, profRes, usersRes, pricingRes]) => {
      const o = orgRes.data || {};
      setOrg({ name: o.name || '', address: o.address || '', phone: o.phone || '', logo_url: o.logo_url || '' });
      if (o.logo_url) setLogoPreview(o.logo_url);
      const p = profRes.data || {};
      setProfile({ name: p.name || '', email: p.email || '', phone: p.phone || '' });
      setPrefs({
        timezone: o.timezone || 'America/Toronto',
        currency: o.currency || 'CAD',
        distance_unit: o.distance_unit || 'km',
      });
      const members = usersRes.data || [];
      setTeamMembers(members);
      const perms = {};
      members.forEach(m => { perms[m.user_id] = m.permissions || []; });
      setMemberPermissions(perms);
      const pr = pricingRes.data || {};
      const p2 = {};
      Object.keys(pricing).forEach(k => { p2[k] = pr[k] != null ? String(pr[k]) : pricing[k]; });
      setPricing(p2);
      setPricingLoaded(true);
    }).finally(() => setLoading(false));
  }, [token]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    try {
      const path = `logos/${user.org_id}_${Date.now()}.${file.name.split('.').pop()}`;
      const { data, error } = await supabase.storage.from('uploads').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('uploads').getPublicUrl(data.path);
      setOrg(prev => ({ ...prev, logo_url: urlData.publicUrl }));
    } catch (err) { console.error('Logo upload failed:', err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
            const pricingData = {};
      Object.entries(pricing).forEach(([k, v]) => {
        if (k === 'default_tax_type') pricingData[k] = v;
        else if (v !== '' && v != null) pricingData[k] = parseFloat(v);
      });
      await Promise.all([
        api.put('/organizations', { ...org, ...prefs }).catch(() => {}),
        api.put('/profile', { name: profile.name, phone: profile.phone }).catch(() => {}),
        api.put('/pricing-rules', pricingData).catch(() => {}),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error('Save failed:', err); }
    finally { setSaving(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Email required'); return; }
    if (!invitePassword || invitePassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setInviting(true);
    try {
      const res = await api.post('/users', { email: inviteEmail, name: inviteEmail.split('@')[0], role: inviteRole, password: invitePassword });
      toast.success('Team member invited! Invite email sent.');
      setInviteEmail('');
      setInvitePassword('');
      setTeamMembers(prev => [...prev, res.data]);
      setMemberPermissions(prev => ({ ...prev, [res.data.user_id]: res.data.permissions || [] }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to invite'); }
    finally { setInviting(false); }
  };

  const togglePermission = (userId, pageKey) => {
    setMemberPermissions(prev => {
      const current = prev[userId] || [];
      const updated = current.includes(pageKey)
        ? current.filter(p => p !== pageKey)
        : [...current, pageKey];
      return { ...prev, [userId]: updated };
    });
  };

  const savePermissions = async (userId) => {
    setSavingPerms(userId);
    try {
      await api.put(`/users/${userId}/permissions`, { permissions: memberPermissions[userId] || [] });
      toast.success('Permissions saved');
    } catch (err) { toast.error('Failed to save permissions'); }
    finally { setSavingPerms(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your company and profile settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="bg-brand-accent text-brand-accent-foreground font-bold tracking-wider hover:bg-brand-accent/80 gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'SAVING...' : saved ? 'SAVED' : 'SAVE CHANGES'}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Vertical Side Menu */}
        <nav className="w-40 flex-shrink-0">
          <div className="space-y-1">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                  activeTab === tab.id
                    ? 'bg-brand-accent/10 text-brand-accent border-l-2 border-brand-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content Area */}
        <div className="flex-1 space-y-6 min-w-0">
          {activeTab === 'profile' && <ProfileTab
            org={org} setOrg={setOrg} profile={profile} setProfile={setProfile}
            prefs={prefs} setPrefs={setPrefs} theme={theme} setTheme={setTheme}
            logoPreview={logoPreview} handleLogoUpload={handleLogoUpload}
          />}
          {activeTab === 'team' && <TeamTab
            inviteEmail={inviteEmail} setInviteEmail={setInviteEmail}
            inviteRole={inviteRole} setInviteRole={setInviteRole}
            invitePassword={invitePassword} setInvitePassword={setInvitePassword}
            showInvitePassword={showInvitePassword} setShowInvitePassword={setShowInvitePassword}
            inviting={inviting} handleInvite={handleInvite}
            teamMembers={teamMembers} memberPermissions={memberPermissions}
            togglePermission={togglePermission} savePermissions={savePermissions}
            savingPerms={savingPerms}
          />}
          {activeTab === 'pricing' && <PricingTab pricing={pricing} setPricing={setPricing} />}
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ org, setOrg, profile, setProfile, prefs, setPrefs, theme, setTheme, logoPreview, handleLogoUpload }) {
  return (
    <>
      {/* Company Profile */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Building2 className="w-4 h-4 text-brand-accent" /> Company Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-6">
            <div className="flex flex-col items-center gap-2">
              <div className="w-20 h-20 rounded-xl bg-muted border border-input flex items-center justify-center overflow-hidden relative group cursor-pointer">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-brand-accent">{org.name?.charAt(0)?.toUpperCase() || 'C'}</span>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-foreground/10 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="w-5 h-5 text-foreground" />
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
              <span className="text-[10px] text-muted-foreground">Click to upload</span>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div><Label className="text-xs text-muted-foreground">Company Name</Label>
                <Input value={org.name} onChange={e => setOrg({ ...org, name: e.target.value })} className="bg-muted border-input text-foreground mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Phone</Label>
                <Input value={org.phone} onChange={e => setOrg({ ...org, phone: e.target.value })} className="bg-muted border-input text-foreground mt-1" /></div>
              <div className="col-span-2"><Label className="text-xs text-muted-foreground">Address</Label>
                <Input value={org.address} onChange={e => setOrg({ ...org, address: e.target.value })} className="bg-muted border-input text-foreground mt-1" /></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Profile */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <User className="w-4 h-4 text-brand-accent" /> Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} className="bg-muted border-input text-foreground mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Email</Label>
              <Input value={profile.email} disabled className="bg-muted/50 border-input text-muted-foreground mt-1 cursor-not-allowed" /></div>
            <div><Label className="text-xs text-muted-foreground">Phone</Label>
              <Input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} className="bg-muted border-input text-foreground mt-1" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Globe className="w-4 h-4 text-brand-accent" /> Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Timezone</Label>
              <select value={prefs.timezone} onChange={e => setPrefs({ ...prefs, timezone: e.target.value })}
                className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 appearance-none cursor-pointer">
                <option value="America/Toronto">Eastern (Toronto)</option>
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Vancouver">Pacific (Vancouver)</option>
                <option value="America/Edmonton">Mountain (Edmonton)</option>
                <option value="America/Winnipeg">Central (Winnipeg)</option>
                <option value="America/Halifax">Atlantic (Halifax)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Currency</Label>
              <select value={prefs.currency} onChange={e => setPrefs({ ...prefs, currency: e.target.value })}
                className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 appearance-none cursor-pointer">
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="USD">USD - US Dollar</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Distance Unit</Label>
              <div className="flex gap-2 mt-1">
                {['km', 'miles'].map(u => (
                  <button key={u} onClick={() => setPrefs({ ...prefs, distance_unit: u })}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors ${
                      prefs.distance_unit === u ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'bg-muted text-muted-foreground border border-input hover:text-foreground'
                    }`}>{u.toUpperCase()}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Theme</Label>
              <div className="flex gap-2 mt-1">
                {[{ id: 'dark', icon: Moon, label: 'Dark' }, { id: 'light', icon: Sun, label: 'Light' }].map(t => (
                  <button key={t.id} onClick={() => setTheme(t.id)}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                      theme === t.id ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'bg-muted text-muted-foreground border border-input hover:text-foreground'
                    }`}><t.icon className="w-3.5 h-3.5" /> {t.label}</button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function TeamTab({ inviteEmail, setInviteEmail, inviteRole, setInviteRole, invitePassword, setInvitePassword, showInvitePassword, setShowInvitePassword, inviting, handleInvite, teamMembers, memberPermissions, togglePermission, savePermissions, savingPerms }) {
  return (
    <>
      {/* Invite */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Send className="w-4 h-4 text-brand-accent" /> Invite Team Member
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Email address"
              className="bg-muted border-input text-foreground flex-1" onKeyDown={e => e.key === 'Enter' && handleInvite()} />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 appearance-none cursor-pointer w-32">
              <option value="admin">Admin</option>
              <option value="dispatcher">Dispatcher</option>
            </select>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">Password</Label>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <Input type={showInvitePassword ? 'text' : 'password'} value={invitePassword} onChange={e => setInvitePassword(e.target.value)} className="bg-muted border-input text-foreground rounded-lg pr-9" placeholder="Min 6 characters" />
                <button type="button" onClick={() => setShowInvitePassword(!showInvitePassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showInvitePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button type="button" variant="outline" className="border-input text-muted-foreground text-xs px-3 shrink-0" onClick={() => setInvitePassword(Math.random().toString(36).slice(2, 12))}>Generate</Button>
            </div>
          </div>
          <Button onClick={handleInvite} disabled={inviting} className="bg-brand-accent text-brand-accent-foreground font-bold px-4">
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Invite</>}
          </Button>
        </CardContent>
      </Card>

      {/* Team Members List */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Users className="w-4 h-4 text-brand-accent" /> Team Members
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {teamMembers.length === 0 && (
            <p className="text-xs text-muted-foreground">No team members yet. Invite someone above.</p>
          )}
          {teamMembers.filter(m => m.role === 'admin' || m.role === 'dispatcher').map(member => (
            <div key={member.user_id} className="bg-muted/50 border border-input rounded-xl p-4 space-y-3">
              {/* Member Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-sm font-bold text-brand-accent">{member.name?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{member.name || 'Unnamed'}</p>
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-md ${
                  member.role === 'admin' ? 'bg-brand-accent/10 text-brand-accent' : 'bg-blue-500/10 text-blue-400'
                }`}>{member.role?.toUpperCase()}</span>
              </div>

              {/* Page Access */}
              <div>
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2 flex items-center gap-1.5">
                  <Shield className="w-3 h-3" /> PAGE ACCESS
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {ALL_PAGES.map(page => {
                    const isActive = (memberPermissions[member.user_id] || []).includes(page.key);
                    return (
                      <button key={page.key} onClick={() => togglePermission(member.user_id, page.key)}
                        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors text-left ${
                          isActive
                            ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20'
                            : 'bg-surface text-muted-foreground border border-input hover:text-foreground hover:border-muted-foreground'
                        }`}>
                        {isActive ? '\u25C9' : '\u25CB'} {page.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-1">
                <Button onClick={() => savePermissions(member.user_id)} disabled={savingPerms === member.user_id}
                  className="bg-muted hover:bg-muted/80 text-foreground text-xs px-3 py-1.5 h-auto">
                  {savingPerms === member.user_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  Save Permissions
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  );
}

function PricingTab({ pricing, setPricing }) {
  const f = (key) => ({
    type: "number", step: "0.01", value: pricing[key],
    onChange: e => setPricing({ ...pricing, [key]: e.target.value }),
    className: "bg-muted border-input text-foreground mt-1",
  });
  return (
    <>
      {/* Base Rates */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-brand-accent" /> Base Rates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs text-muted-foreground">Base Rate ($/load)</Label><Input {...f('base_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Price per KM</Label><Input {...f('price_per_km')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Price per KG</Label><Input {...f('price_per_kg')} placeholder="0.00" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Charge Type Default Rates */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-brand-accent" /> Charge Type Rates
          </CardTitle>
          <p className="text-[10px] text-muted-foreground mt-1">Default rates used when auto-generating charges for new loads</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs text-muted-foreground">Weight Charge ($/lb)</Label><Input {...f('weight_rate_per_lb')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Pieces Charge ($/piece)</Label><Input {...f('pieces_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Vehicle Charge ($)</Label><Input {...f('vehicle_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">After Hours Rate ($)</Label><Input {...f('after_hours_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Waiting Time - Shipper ($/hr)</Label><Input {...f('waiting_rate_shipper')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Waiting Time - Consignee ($/hr)</Label><Input {...f('waiting_rate_consignee')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Insurance Charge ($)</Label><Input {...f('insurance_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Appointment Charge ($)</Label><Input {...f('appointment_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">MISC Accessorial ($)</Label><Input {...f('misc_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Dangerous Goods/Hazmat ($)</Label><Input {...f('hazmat_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">Signature Charge ($)</Label><Input {...f('signature_rate')} placeholder="0.00" /></div>
            <div><Label className="text-xs text-muted-foreground">POD/Document Charge ($)</Label><Input {...f('pod_photo_rate')} placeholder="0.00" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Multipliers */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground">Delivery Type Multipliers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs text-muted-foreground">Regular (multiplier)</Label><Input {...f('rate_regular')} /></div>
            <div><Label className="text-xs text-muted-foreground">Rush / Hot Shot (multiplier)</Label><Input {...f('rate_rush')} /></div>
            <div><Label className="text-xs text-muted-foreground">Direct (multiplier)</Label><Input {...f('rate_direct')} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Tax Configuration */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground">Tax Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Default Tax Type</Label>
              <select value={pricing.default_tax_type} onChange={e => setPricing({ ...pricing, default_tax_type: e.target.value })}
                className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 appearance-none cursor-pointer">
                <option value="gst">GST</option>
                <option value="hst">HST</option>
                <option value="pst">PST</option>
              </select>
            </div>
            <div><Label className="text-xs text-muted-foreground">GST Rate (%)</Label><Input {...f('gst_rate')} /></div>
            <div><Label className="text-xs text-muted-foreground">HST Rate (%)</Label><Input {...f('hst_rate')} /></div>
            <div><Label className="text-xs text-muted-foreground">PST Rate (%)</Label><Input {...f('pst_rate')} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Fuel & Dimensions */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground">Fuel Surcharge & Dimensions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div><Label className="text-xs text-muted-foreground">Fuel Surcharge (%)</Label><Input {...f('fuel_surcharge_pct')} placeholder="15" /></div>
            <div><Label className="text-xs text-muted-foreground">Wait Time ($/hr)</Label><Input {...f('wait_time_per_hour')} placeholder="75" /></div>
            <div><Label className="text-xs text-muted-foreground">Max Width (ft)</Label><Input {...f('max_width')} placeholder="8.5" /></div>
            <div><Label className="text-xs text-muted-foreground">Max Height (ft)</Label><Input {...f('max_height')} placeholder="13.5" /></div>
            <div><Label className="text-xs text-muted-foreground">Max Length (ft)</Label><Input {...f('max_length')} placeholder="53" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Automation Rules */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-muted-foreground">Automation Rules</CardTitle>
          <p className="text-[10px] text-muted-foreground mt-1">Configure when charges are automatically applied</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">WAIT TIME RULES</p>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-xs text-muted-foreground">Grace Period (minutes)</Label><Input {...f('wait_grace_minutes')} placeholder="30" /></div>
              <div>
                <Label className="text-xs text-muted-foreground">Min Charge Increment</Label>
                <select value={pricing.wait_min_increment} onChange={e => setPricing({ ...pricing, wait_min_increment: e.target.value })}
                  className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 appearance-none cursor-pointer">
                  <option value="15min">15 minutes</option>
                  <option value="30min">30 minutes</option>
                  <option value="1hr">1 hour</option>
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={pricing.wait_auto_apply === true || pricing.wait_auto_apply === 'true'}
                    onChange={e => setPricing({ ...pricing, wait_auto_apply: e.target.checked })} className="rounded" />
                  Auto-apply wait charges
                </label>
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">FUEL SURCHARGE RULES</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={pricing.fuel_auto_apply === true || pricing.fuel_auto_apply === 'true'}
                    onChange={e => setPricing({ ...pricing, fuel_auto_apply: e.target.checked })} className="rounded" />
                  Auto-apply fuel surcharge
                </label>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Apply To</Label>
                <select value={pricing.fuel_apply_to} onChange={e => setPricing({ ...pricing, fuel_apply_to: e.target.value })}
                  className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 appearance-none cursor-pointer">
                  <option value="base_only">Base charge only</option>
                  <option value="all_charges">All charges</option>
                </select>
              </div>
              <div><Label className="text-xs text-muted-foreground">Min Distance (km)</Label><Input {...f('fuel_min_distance_km')} placeholder="0" /></div>
            </div>
          </div>

          <div>
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">AFTER HOURS RULES</p>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">After Hours Start</Label>
                <Input type="time" value={pricing.after_hours_start} onChange={e => setPricing({ ...pricing, after_hours_start: e.target.value })}
                  className="bg-muted border-input text-foreground mt-1" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">After Hours End</Label>
                <Input type="time" value={pricing.after_hours_end} onChange={e => setPricing({ ...pricing, after_hours_end: e.target.value })}
                  className="bg-muted border-input text-foreground mt-1" />
              </div>
              <div><Label className="text-xs text-muted-foreground">Weekend Multiplier</Label><Input {...f('weekend_multiplier')} /></div>
              <div><Label className="text-xs text-muted-foreground">Holiday Multiplier</Label><Input {...f('holiday_multiplier')} /></div>
            </div>
          </div>

          <div>
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">AUTO-CHARGE SETTINGS</p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={pricing.auto_generate_charges === true || pricing.auto_generate_charges === 'true'}
                  onChange={e => setPricing({ ...pricing, auto_generate_charges: e.target.checked })} className="rounded" />
                Auto-generate charges on new loads
              </label>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={pricing.auto_apply_tax === true || pricing.auto_apply_tax === 'true'}
                  onChange={e => setPricing({ ...pricing, auto_apply_tax: e.target.checked })} className="rounded" />
                Auto-apply tax to charges
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
